import React, { useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useGetPromoByIdQuery } from "../../../providers/promo";
import { useGetIncludeProductsQuery, useGetProductsInfiniteQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import TemplatePage from "../template";
import { AdditionalFeature, Product } from "@core/types";
import { useSearchParams } from "../../../hooks/useSearchParams";
import useGetProductsQuery from "../../../providers/products/useGetProductsQuery";

interface RouteParams {
  id: string;
  name: string;
  type: string;
}

const PromoPage = () => {
  const params = useParams<RouteParams>();
  const infiniteRef = useRef<HTMLIonInfiniteScrollElement | null>(null);
  const { get } = useSearchParams();
  const filterValue = get("additionalProductFilter");
  const currentFilter = filterValue === null ? undefined : filterValue;
  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();
  const { data: promo, isLoading: isPromoLoading } = useGetPromoByIdQuery({ id: params.id! });
  const includeProductsEnabled = !!promo?.productIds?.length;
  const fetchProductEnabled = !!promo?.category?.id && !promo?.productIds?.length;
  const enableCategoryProducts = !!promo?.category?.id && !!activeShippingZone.id && !promo?.productIds?.length;

  const { data: fetchedIncludeProducts, isLoading: isIncludeProductsLoading } = useGetIncludeProductsQuery({
    include: promo?.productIds?.map(String) || [],
    params: {
      zoneId: activeShippingZone.id,
      per_page: "12",
    },
    options: {
      enabled: includeProductsEnabled,
    },
  });

  const {
    data: fetchedProducts,
    fetchNextPage,
    hasNextPage,
    isLoading: isProductsLoading,
    isFetchingNextPage,
  } = useGetProductsInfiniteQuery({
    options: {
      enabled: fetchProductEnabled,
    },
    params: {
      pageNumber: "1",
      pageSize: "12",
      categoryId: promo?.category?.id,
      zoneId: activeShippingZone.id,
      additionalProductFilter: currentFilter,
    },
  });

  const { data: categoryProductsResults, isLoading: isCategoryProductsLoading } = useGetProductsQuery(
    {
      zoneId: activeShippingZone.id,
      categoryId: promo?.category?.id,
    },
    {
      enabled: enableCategoryProducts,
    },
  );

  const productsList = useMemo(() => {
    const productsMap = new Map<string | number, Product>();

    const products = fetchedProducts?.pages.length
      ? fetchedProducts?.pages.flatMap((page) => page?.products)
      : categoryProductsResults?.products;

    products?.forEach((product) => {
      if (product && !productsMap.has(product.id)) {
        productsMap.set(product.id, product);
      }
    });

    return Array.from(productsMap.values());
  }, [fetchedProducts, categoryProductsResults]);

  const additionalFilters = useMemo(() => {
    const productsMap = new Map<string | number, AdditionalFeature>();

    const additionalFeatures = fetchedProducts?.pages.length
      ? fetchedProducts?.pages.flatMap((page) => page?.additionalFeatures)
      : categoryProductsResults?.additionalFeatures;

    additionalFeatures?.forEach((additionalFeature) => {
      if (additionalFeature && !productsMap.has(additionalFeature.additionalFeature)) {
        productsMap.set(additionalFeature.additionalFeature, additionalFeature);
      }
    });

    return Array.from(productsMap.values());
  }, [fetchedProducts, categoryProductsResults]);

  const promoProducts = fetchedIncludeProducts?.length ? fetchedIncludeProducts : productsList;

  const promoName = promo?.title || params.name;
  const hasProducts = promoProducts?.length ? promoProducts?.length > 0 : false;
  const isInfiniteScrollDisabled = !hasNextPage && !isFetchingNextPage;

  const isIncludeEnabledAndLoading = includeProductsEnabled && isIncludeProductsLoading;
  const isInfiniteEnabledAndLoading = fetchProductEnabled && isProductsLoading;
  const isCategoryProductEnabledAndLoading = enableCategoryProducts && isCategoryProductsLoading;

  const isLoading =
    isPromoLoading ||
    isIncludeEnabledAndLoading ||
    isInfiniteEnabledAndLoading ||
    isCategoryProductEnabledAndLoading ||
    isActiveShippingZoneLoading;

  const renderPromoDescription = () => {
    if (!promo?.description) {
      return null;
    }
    return <p className="font-normal text-sm" dangerouslySetInnerHTML={{ __html: promo?.description }} />;
  };

  const handleShowMoreClick = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  const handleIonInfinite = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
    requestAnimationFrame(() => {
      infiniteRef.current?.complete();
    });
  };

  const base64Image = promo?.images?.[0]?.base64 || "";

  return (
    <TemplatePage
      title={promoName}
      filters={additionalFilters}
      description={renderPromoDescription()}
      imageSrc={promo?.images?.[0]?.src || ""}
      products={promoProducts}
      hasProducts={hasProducts}
      isLoading={isLoading}
      infiniteRef={infiniteRef}
      isInfiniteScrollDisabled={isInfiniteScrollDisabled}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onShowMoreClick={handleShowMoreClick}
      onIonInfinite={handleIonInfinite}
      showNoFoundProducts={false}
      base64Image={base64Image}
    />
  );
};

export default PromoPage;
